import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import MESSAGES from '../../../../common/data/constants/messages';
import { IEmployee } from '../../../../common/data/dtos/employee';
import Badge from '../../../../components/bootstrap/Badge';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import { MoreActions } from '../../../../components/extras/MoreActions';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import PaginationButtons, { dataPagination, PER_COUNT } from '../../../../components/PaginationButtons';
import { hiddenMenu } from '../../../../menu';
import employeeService from '../../../../services/employee-service';
import LoadingPage from '../../loading/LoadingPage';
import { EmployeeModal } from './EmployeeModal';

export const CommonEmployeeList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_COUNT['10']);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);

    const [employees, setEmployees] = useState<IEmployee[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<IEmployee | null>(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        setIsLoading(true);

        const response = await employeeService.getEmployees();

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        setEmployees(response.employees || []);
    };

    const toggleModal = (isVisible: boolean) => {
        setIsModalOpened(isVisible);
    };

    const handleAddClick = () => {
        toggleModal(true);
    };

    const onModalClose = () => {
        toggleModal(false);
        setSelectedEmployee(null);
    };

    const onAfterSubmit = async () => {
        await fetchEmployees();
        onModalClose();
    };

    const handleDeleteClick = async (company: IEmployee) => {
        if (!company.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await employeeService.deleteEmployee(company.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await fetchEmployees();
    };

    const handleEditClick = (company: IEmployee) => {
        setSelectedEmployee(company);
        toggleModal(true);
    };

    const handleDeliveryClick = async (employee: IEmployee) => {
        if (employee.id) navigate(`../${hiddenMenu.workResourceEmployees.path.replace(':id', employee.id)}`);
    };

    const handleTrainingClick = async (employee: IEmployee) => {
        if (employee.id) navigate(`../${hiddenMenu.trainingEmployees.path.replace(':id', employee.id)}`);
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            <Card style={{ minHeight: 600 }}>
                <CardHeader borderSize={1}>
                    <CardLabel icon="PeopleAlt" iconColor="primary">
                        <CardTitle tag="div" className="h5">
                            Colaboradores
                        </CardTitle>
                    </CardLabel>
                    <CardActions>
                        <Button color="light" icon="AddCircle" onClick={handleAddClick}>
                            Adicionar
                        </Button>
                    </CardActions>
                </CardHeader>
                <CardBody className="table-responsive">
                    <table className="table table-modern table-hover">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Matrícula</th>
                                <th>E-mail</th>
                                <th>Data Admissão</th>
                                {/* <th>Primeiro Acesso</th> */}
                                <th>Situação</th>
                                <td aria-labelledby="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {dataPagination(employees, currentPage, perPage).map((item: IEmployee) => (
                                <tr key={item.id}>
                                    <td>{item.name}</td>
                                    <td>{item.registrationNumber}</td>
                                    <td>{item.email}</td>
                                    <td>{item.admissionDate ? dayjs(item.admissionDate).format('DD/MM/YYYY') : '-'}</td>
                                    {/* <td className="text-center"> */}
                                    {/*     {item.firstAccess ? ( */}
                                    {/*         <Icon icon="Check" color="danger" /> */}
                                    {/*     ) : ( */}
                                    {/*         <Icon icon="Close" color="success" /> */}
                                    {/*     )} */}
                                    {/* </td> */}
                                    <td>
                                        {item.status ? (
                                            <h5>
                                                <Badge color="success" className="align-items-center justify-content-center">
                                                    Ativo
                                                </Badge>
                                            </h5>
                                        ) : (
                                            <h5>
                                                <Badge color="danger" className="align-items-center justify-content-center">
                                                    Inativo
                                                </Badge>
                                            </h5>
                                        )}
                                    </td>

                                    <MoreActions
                                        id={item.id}
                                        showEdit={true}
                                        showDelete={true}
                                        onEditClick={() => handleEditClick(item)}
                                        onDeleteClick={() => handleDeleteClick(item)}
                                        customActions={[
                                            {
                                                label: 'Fornecimento',
                                                icon: 'Build',
                                                onClickAction: () => handleDeliveryClick(item),
                                            },
                                            {
                                                label: 'Treinamentos',
                                                icon: 'School',
                                                onClickAction: () => handleTrainingClick(item),
                                            },
                                        ]}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardBody>
                <PaginationButtons data={employees} label="items" setCurrentPage={setCurrentPage} currentPage={currentPage} perPage={perPage} setPerPage={setPerPage} />
            </Card>

            <EmployeeModal isModalOpened={isModalOpened} onModalClose={onModalClose} onAfterSubmit={onAfterSubmit} selectedEmployee={selectedEmployee} />
        </>
    );
};
